import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import Cookies from 'js-cookie'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import DashboardIcon from '@mui/icons-material/DashboardOutlined'
import InicioIcon from '@mui/icons-material/HomeOutlined'
import ComptradeIcon from '@mui/icons-material/StorefrontOutlined'
import KitIcon from '@mui/icons-material/DescriptionOutlined'
import CatalogoIcon from '@mui/icons-material/GridViewOutlined'
import RegrasIcon from '@mui/icons-material/RuleOutlined'
import AgentesIcon from '@mui/icons-material/AutoAwesomeOutlined'
import TemplatesIcon from '@mui/icons-material/BrushOutlined'
import EquipesIcon from '@mui/icons-material/GroupsOutlined'
import PessoasIcon from '@mui/icons-material/PersonOutlineOutlined'
import MarcasIcon from '@mui/icons-material/PaletteOutlined'
import AmbientesIcon from '@mui/icons-material/ApartmentOutlined'
import RecolherIcon from '@mui/icons-material/KeyboardDoubleArrowLeft'
import ExpandirIcon from '@mui/icons-material/KeyboardDoubleArrowRight'
import MenuIcon from '@mui/icons-material/Menu'
import SairIcon from '@mui/icons-material/Logout'
import GuiaIcon from '@mui/icons-material/HelpOutlineOutlined'
import BuscarIcon from '@mui/icons-material/Search'
import SolicitacoesIcon from '@mui/icons-material/SupportAgentOutlined'
import FilaIcon from '@mui/icons-material/ViewKanbanOutlined'
import MinhaFilaIcon from '@mui/icons-material/FormatListNumberedOutlined'
import BuscaGlobal from './BuscaGlobal'
import ProtecaoDeTela from './ProtecaoDeTela'
import Sino from './Sino'
import GuiaDoPortal from './guia/GuiaDoPortal'
import { guiaVisto, marcarGuiaVisto } from './guia/guia'
import { passosDaPessoa } from './guia/passos'
import { useAuth } from '../hooks/useAuth'
import { carregarNovoPortal, limparNovoPortal, useNovoPortal } from './estado'
import { aplicarMarca, FONTES, limparMarca, logoDaMarca, marcaDaCasa, marcaDoAmbiente } from './tema'
import './casca.scss'

interface ItemMenu {
  rotulo: string
  para: string
  icone: React.ReactNode
  ativo: (caminho: string) => boolean
  claims: string[]
  /** Nome do item no guia de boas-vindas (atributo data-guia). */
  guia?: string
}

const PRINCIPAL: ItemMenu[] = [
  { rotulo: 'Início', para: '/app/inicio', icone: <InicioIcon />, ativo: c => c.startsWith('/app/inicio'), claims: ['showmenu_bi_dashboards'], guia: 'inicio' },
  { rotulo: 'Dashboards', para: '/app/dashboard', icone: <DashboardIcon />, ativo: c => c.startsWith('/app/dashboard'), claims: ['showmenu_bi_dashboards'], guia: 'dashboards' },
  { rotulo: 'Comptrade', para: '/app/comptrade/est', icone: <ComptradeIcon />, ativo: c => c.startsWith('/app/comptrade'), claims: ['comptrade_read_est'] },
  { rotulo: 'Kit de documentos', para: '/app/trct/list', icone: <KitIcon />, ativo: c => c.startsWith('/app/trct'), claims: ['showmenu_trct'] }
]

// Tudo do módulo de chamados num grupo só: abrir e acompanhar (todos) e a fila de atendimento (quem atende).
const CHAMADOS: ItemMenu[] = [
  { rotulo: 'Solicitações', para: '/app/solicitacoes', icone: <SolicitacoesIcon />, ativo: c => c.startsWith('/app/solicitacoes'), claims: ['solicitacoes', 'gestao_solicitacoes', 'bi_dashboards_read_any'], guia: 'solicitacoes' },
  { rotulo: 'Fila de atendimento', para: '/app/gestao/solicitacoes', icone: <FilaIcon />, ativo: c => c.startsWith('/app/gestao/solicitacoes'), claims: ['gestao_solicitacoes', 'bi_dashboards_read_any'] },
  { rotulo: 'Minha fila', para: '/app/gestao/minha-fila', icone: <MinhaFilaIcon />, ativo: c => c.startsWith('/app/gestao/minha-fila'), claims: ['gestao_solicitacoes', 'bi_dashboards_read_any'] }
]

const GESTAO: ItemMenu[] = [
  { rotulo: 'Dashboards', para: '/app/gestao/dashboards', icone: <CatalogoIcon />, ativo: c => ['/app/gestao/dashboards', '/app/dash_management/functionalities', '/app/dash_management/dashboard'].some(p => c.startsWith(p)), claims: ['showmenu_bi_dashboards_edit'] },
  { rotulo: 'Ambientes', para: '/app/gestao/ambientes', icone: <AmbientesIcon />, ativo: c => ['/app/gestao/ambientes', '/app/dash_management/environment'].some(p => c.startsWith(p)), claims: ['showmenu_bi_dashboards_edit'] },
  { rotulo: 'Regras', para: '/app/gestao/regras', icone: <RegrasIcon />, ativo: c => ['/app/gestao/regras', '/app/dash_management/rules'].some(p => c.startsWith(p)), claims: ['showmenu_bi_dashboards_edit'] },
  { rotulo: 'Pessoas', para: '/app/gestao/pessoas', icone: <PessoasIcon />, ativo: c => c.startsWith('/app/gestao/pessoas'), claims: ['showmenu_bi_dashboards_edit'] },
  { rotulo: 'Equipes', para: '/app/gestao/equipes', icone: <EquipesIcon />, ativo: c => ['/app/gestao/equipes', '/app/dash_management/users'].some(p => c.startsWith(p)), claims: ['showmenu_bi_dashboards_edit'] },
  { rotulo: 'Agentes oneflow', para: '/app/gestao/agentes', icone: <AgentesIcon />, ativo: c => ['/app/gestao/agentes', '/app/dash_management/agents'].some(p => c.startsWith(p)), claims: ['showmenu_bi_dashboards_edit'] },
  { rotulo: 'Templates HTML', para: '/app/gestao/templates', icone: <TemplatesIcon />, ativo: c => ['/app/gestao/templates', '/app/dash_management/html_templates'].some(p => c.startsWith(p)), claims: ['showmenu_bi_dashboards_edit'] },
  { rotulo: 'Marcas', para: '/app/gestao/marcas', icone: <MarcasIcon />, ativo: c => c.startsWith('/app/gestao/marcas'), claims: ['bi_dashboards_read_any'] }
]

function lembrar(chave: string, valor: boolean) { try { localStorage.setItem(chave, valor ? '1' : '0') } catch { /* sem armazenamento */ } }
function lembrado(chave: string) { try { return localStorage.getItem(chave) === '1' } catch { return false } }

export default function CascaNova({ children }: { children: React.ReactNode }) {
  const est = useNovoPortal()
  const { getUserRoles, getUser } = useAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const roles: string[] = getUserRoles() || []
  const usuario = getUser()

  const [compacta, setCompacta] = useState(lembrado('np-compacta'))
  const [gaveta, setGaveta] = useState(false)
  const [busca, setBusca] = useState(false)
  const [guia, setGuia] = useState(false)
  // Sem armazenamento no navegador e com o servidor recusando, o guia ainda não volta na mesma sessão.
  const guiaFechado = useRef(false)

  // Ctrl K (ou Cmd K) abre a busca de qualquer tela; o topo do dashboard também pede por evento.
  useEffect(() => {
    const teclas = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setBusca(true) }
    }
    const pedido = () => setBusca(true)
    window.addEventListener('keydown', teclas)
    window.addEventListener('np-abrir-busca', pedido)
    return () => { window.removeEventListener('keydown', teclas); window.removeEventListener('np-abrir-busca', pedido) }
  }, [])

  useEffect(() => { carregarNovoPortal() }, [])

  // O guia abre sozinho uma vez, na primeira tela de quem entra (início ou dashboards); depois só pelo botão do menu.
  useEffect(() => {
    if (!est.carregado || guiaFechado.current || guiaVisto(est.preferencias, usuario?.id)) return
    if (pathname !== '/app/inicio' && pathname !== '/app/dashboard') return
    const t = setTimeout(() => setGuia(true), 800)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [est.carregado, est.preferencias, pathname])
  useEffect(() => {
    const pedido = () => setGuia(true)
    window.addEventListener('np-abrir-guia', pedido)
    return () => window.removeEventListener('np-abrir-guia', pedido)
  }, [])
  useEffect(() => { setGaveta(false) }, [pathname])

  const ambienteId = Number((/^\/app\/dashboard\/\d+\/(\d+)/.exec(pathname) || [])[1]) || null
  const marca = ambienteId ? marcaDoAmbiente(est.marcas, est.ambientes, ambienteId) : marcaDaCasa(est.marcas, est.ambientes)
  const logo = logoDaMarca(marca)

  useEffect(() => { aplicarMarca(marca) }, [marca])
  useEffect(() => () => limparMarca(), [])

  // Os componentes do MUI (botões, campos) também vestem a marca.
  const tema = useMemo(() => createTheme({
    palette: { primary: { main: marca.corPrincipal }, secondary: { main: marca.corPrincipal } },
    typography: { fontFamily: FONTES[marca.fonte] || FONTES['Nunito Sans'] }
  }), [marca])

  const visiveis = (itens: ItemMenu[]) => itens.filter(i => i.claims.some(c => roles.includes(c)))
  const principal = visiveis(PRINCIPAL), chamados = visiveis(CHAMADOS), gestao = visiveis(GESTAO)
  const podeBuscar = roles.some(r => r === 'bi_dashboards_read' || r === 'bi_dashboards_read_any')
  // Celular: Início e Dashboards à mão, mais a busca e o menu inteiro (gaveta).
  const doCelular = principal.filter(i => i.para === '/app/inicio' || i.para === '/app/dashboard')

  const sair = () => {
    Cookies.remove('TradeTalentosTokenPAT')
    Cookies.remove('TradeTalentosUserPAT')
    limparNovoPortal()
    navigate('/')
  }
  const fecharGuia = () => { guiaFechado.current = true; setGuia(false); marcarGuiaVisto(usuario?.id) }
  const alternarCompacta = () => { lembrar('np-compacta', !compacta); setCompacta(!compacta) }

  const item = (i: ItemMenu) => (
    <Link key={i.para} to={i.para} className="np-nav" data-guia={i.guia} aria-current={i.ativo(pathname) ? 'page' : undefined} title={compacta ? i.rotulo : undefined}>
      {i.icone}<span>{i.rotulo}</span>
    </Link>
  )

  return (
    <ThemeProvider theme={tema}>
      <div className={`np-casca${compacta ? ' compacta' : ''}${gaveta ? ' gaveta-aberta' : ''}`}>
        <aside className="np-lateral" aria-label="Menu principal">
          <div className="np-logo-linha">
            <Link to="/app/inicio" className="np-logo" aria-label={`${marca.nome}, ir para a página inicial`}>
              {logo ? <img src={logo} alt={marca.nome} /> : <span className="np-logo-nome">{marca.nome}</span>}
              <span className={`np-logo-ini${logo ? ' com-logo' : ''}`} aria-hidden="true">{marca.nome.charAt(0)}</span>
            </Link>
            <button type="button" className="np-ico" data-guia="recolher" onClick={alternarCompacta} aria-label={compacta ? 'Abrir o menu' : 'Recolher o menu'} aria-expanded={!compacta}>
              {compacta ? <ExpandirIcon /> : <RecolherIcon />}
            </button>
          </div>
          <nav className="np-navs">
            {podeBuscar && (
              <button type="button" className="np-nav np-nav-busca" data-guia="busca" onClick={() => setBusca(true)} title={compacta ? 'Buscar dashboard (Ctrl K)' : undefined}>
                <BuscarIcon /><span>Buscar</span><kbd>Ctrl K</kbd>
              </button>
            )}
            {principal.map(item)}
            {chamados.length > 0 && <div className="np-nav-sec">Chamados</div>}
            {chamados.map(item)}
            {gestao.length > 0 && <div className="np-nav-sec">Gestão</div>}
            {gestao.map(item)}
          </nav>
          <div className="np-rodape">
            <div data-guia="avisos"><Sino variante="menu" compacta={compacta} /></div>
            {usuario && <div className="np-usuario" title={usuario.email}><span className="np-av">{(usuario.fullName || usuario.userName || '?').charAt(0)}</span><span>{usuario.fullName || usuario.userName}</span></div>}
            <button type="button" className="np-nav" onClick={() => setGuia(true)} title={compacta ? 'Guia do portal' : undefined}><GuiaIcon /><span>Guia do portal</span></button>
            <button type="button" className="np-nav" onClick={sair} title={compacta ? 'Sair' : undefined}><SairIcon /><span>Sair</span></button>
          </div>
        </aside>
        <div className="np-veu" onClick={() => setGaveta(false)} aria-hidden="true" />
        <main className="np-principal">
          <div className="np-topo-celular">
            <Link to="/app/inicio" aria-label={`${marca.nome}, ir para a página inicial`}>{logo ? <img src={logo} alt={marca.nome} /> : <b>{marca.nome}</b>}</Link>
            <Sino variante="topo" />
          </div>
          <ProtecaoDeTela chave={pathname}>{children}</ProtecaoDeTela>
        </main>
        <nav className="np-baixo" aria-label="Navegação">
          {doCelular.map(i => (
            <Link key={i.para} to={i.para} aria-current={i.ativo(pathname) ? 'page' : undefined}>{i.icone}<span>{i.rotulo}</span></Link>
          ))}
          {podeBuscar && <button type="button" onClick={() => setBusca(true)}><BuscarIcon /><span>Buscar</span></button>}
          <button type="button" onClick={() => setGaveta(true)} aria-expanded={gaveta}
                  aria-current={!doCelular.some(i => i.ativo(pathname)) ? 'page' : undefined}><MenuIcon /><span>Menu</span></button>
        </nav>
      </div>
      <BuscaGlobal aberta={busca} aoFechar={() => setBusca(false)} />
      {guia && <GuiaDoPortal passos={passosDaPessoa(roles)} aoFechar={fecharGuia} />}
    </ThemeProvider>
  )
}
